<script setup lang="ts">
/**
 * ImageClusterRenderer
 *
 * Renders cluster markers using HTML elements:
 * - 2x2 grid of images for clusters with multiple images
 * - Single image for clusters with 1 image
 * - Blue circles with count for clusters without images
 * - Click to zoom and expand cluster
 *
 * Based on: https://docs.mapbox.com/mapbox-gl-js/example/cluster-html/
 */
import { onMounted, onUnmounted, watch } from 'vue'
import { debounce } from 'lodash'
import mapboxgl from 'mapbox-gl'
import type { Map as MapboxMap, GeoJSONSource, Marker } from 'mapbox-gl'

const props = defineProps<{
  map: MapboxMap
  sourceId: string
  groupId: string
  isVisible: boolean
}>()

const emit = defineEmits<{
  (e: 'cluster-click', cluster: GeoJSON.Feature, event?: MouseEvent): void
}>()

// Store markers by cluster_id for efficient updates
const markers: Record<string, Marker> = {}
let markersOnScreen: Record<string, Marker> = {}

// Cache for cluster images (clusterId -> thumbUrls[])
const clusterImagesCache: Record<number, string[]> = {}

/**
 * Create HTML element for a cluster marker with 2x2 image grid
 */
function createClusterElement(
  thumbUrls: string[],
  count: number,
  clusterId: number
): HTMLElement {
  const el = document.createElement('div')
  el.className = 'image-cluster-marker'
  el.dataset.clusterId = String(clusterId)

  if (thumbUrls.length > 0) {
    // Determine grid layout based on number of images
    const gridClass = thumbUrls.length >= 4 ? 'grid-2x2' :
                      thumbUrls.length >= 2 ? 'grid-2x1' : 'grid-1x1'

    // Take up to 4 images
    const images = thumbUrls.slice(0, 4)

    // Create grid HTML
    const imageGridHtml = images.map((url, i) =>
      `<div class="cluster-grid-cell" data-index="${i}"></div>`
    ).join('')

    el.innerHTML = `
      <div class="cluster-image-container ${gridClass} loading">
        <div class="cluster-grid">${imageGridHtml}</div>
        <div class="cluster-count">${formatCount(count)}</div>
      </div>
    `

    const container = el.querySelector('.cluster-image-container') as HTMLElement
    const cells = el.querySelectorAll('.cluster-grid-cell') as NodeListOf<HTMLElement>

    let loadedCount = 0
    let errorCount = 0

    // Load each image
    images.forEach((url, index) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        if (cells[index]) {
          cells[index].style.backgroundImage = `url('${url}')`
        }
        loadedCount++
        if (loadedCount + errorCount === images.length) {
          container.classList.remove('loading')
          container.classList.add('loaded')
        }
      }

      img.onerror = () => {
        errorCount++
        if (cells[index]) {
          cells[index].style.background = '#e5e7eb'
        }
        if (loadedCount + errorCount === images.length) {
          container.classList.remove('loading')
          if (loadedCount === 0) {
            // All images failed, show circle fallback
            el.innerHTML = `
              <div class="cluster-circle">
                <span class="cluster-circle-count">${formatCount(count)}</span>
              </div>
            `
          } else {
            container.classList.add('loaded')
          }
        }
      }

      img.src = url
    })
  } else {
    // Circle fallback without images
    el.innerHTML = `
      <div class="cluster-circle">
        <span class="cluster-circle-count">${formatCount(count)}</span>
      </div>
    `
  }

  return el
}

/**
 * Format count for display (e.g., 1000 -> 1K)
 */
function formatCount(count: number): string {
  if (count >= 10000) return Math.floor(count / 1000) + 'K'
  if (count >= 1000) return (count / 1000).toFixed(1).replace(/\.0$/, '') + 'K'
  return String(count)
}

/**
 * Get thumbUrls from cluster leaves
 */
function getClusterImages(clusterId: number, callback: (urls: string[]) => void): void {
  // Check cache first
  if (clusterImagesCache[clusterId]) {
    callback(clusterImagesCache[clusterId])
    return
  }

  const source = props.map.getSource(props.sourceId) as GeoJSONSource
  if (!source) {
    callback([])
    return
  }

  // Get up to 10 leaves to find 4 with images
  source.getClusterLeaves(clusterId, 10, 0, (error, features) => {
    if (error || !features) {
      callback([])
      return
    }

    // Extract unique thumbUrls
    const urls: string[] = []
    for (const feature of features) {
      const thumbUrl = feature.properties?.thumbUrl
      if (thumbUrl && !urls.includes(thumbUrl)) {
        urls.push(thumbUrl)
        if (urls.length >= 4) break
      }
    }

    // Cache the result
    clusterImagesCache[clusterId] = urls
    callback(urls)
  })
}

/**
 * Handle cluster click - zoom to expansion level
 */
function handleClusterClick(clusterId: number, coordinates: [number, number]): void {
  const source = props.map.getSource(props.sourceId) as GeoJSONSource
  if (!source) return

  source.getClusterExpansionZoom(clusterId, (error, zoom) => {
    if (error || zoom === null || zoom === undefined) return

    props.map.easeTo({
      center: coordinates,
      zoom: zoom
    })
  })
}

/**
 * Update markers based on visible clusters
 */
function updateMarkers(): void {
  if (!props.isVisible) {
    // Hide all markers when layer is not visible
    Object.values(markersOnScreen).forEach(marker => marker.remove())
    markersOnScreen = {}
    return
  }

  const source = props.map.getSource(props.sourceId) as GeoJSONSource
  if (!source) return

  const newMarkers: Record<string, Marker> = {}
  const features = props.map.querySourceFeatures(props.sourceId)
  const seen = new Set<number>()

  // Process each feature
  for (const feature of features) {
    const geometry = feature.geometry as GeoJSON.Point
    if (!geometry || geometry.type !== 'Point') continue

    const coordinates = geometry.coordinates as [number, number]
    const featureProps = feature.properties

    // Only process clusters (features with point_count)
    if (!featureProps?.cluster) continue

    const clusterId = featureProps.cluster_id as number
    const count = featureProps.point_count as number
    if (seen.has(clusterId)) continue
    seen.add(clusterId)

    // Check if we already have a marker for this cluster
    let marker = markers[clusterId]

    if (!marker) {
      // Create placeholder marker first
      const el = document.createElement('div')
      el.className = 'image-cluster-marker'
      el.innerHTML = `
        <div class="cluster-image-container loading">
          <div class="cluster-grid"></div>
          <div class="cluster-count">${formatCount(count)}</div>
        </div>
      `

      marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      }).setLngLat(coordinates)

      // Add click handler
      el.addEventListener('click', (e) => {
        e.stopPropagation()
        handleClusterClick(clusterId, coordinates)
        emit('cluster-click', feature)
      })

      markers[clusterId] = marker

      // Fetch cluster images asynchronously
      getClusterImages(clusterId, (urls) => {
        // Replace placeholder with actual content
        const newEl = createClusterElement(urls, count, clusterId)

        // Copy click handler
        newEl.addEventListener('click', (e) => {
          e.stopPropagation()
          handleClusterClick(clusterId, coordinates)
          emit('cluster-click', feature)
        })

        // Update marker element
        const existingMarker = markers[clusterId]
        if (existingMarker) {
          existingMarker.remove()
          const updatedMarker = new mapboxgl.Marker({
            element: newEl,
            anchor: 'center'
          }).setLngLat(coordinates)

          markers[clusterId] = updatedMarker

          if (markersOnScreen[clusterId]) {
            updatedMarker.addTo(props.map)
            markersOnScreen[clusterId] = updatedMarker
          }
        }
      })
    } else {
      // Update position if marker already exists
      marker.setLngLat(coordinates)
    }

    newMarkers[clusterId] = marker

    // Add to map if not already on screen
    if (!markersOnScreen[clusterId]) {
      marker.addTo(props.map)
    }
  }

  // Remove markers that are no longer visible and clean up their cache entries
  for (const id in markersOnScreen) {
    if (!newMarkers[id]) {
      markersOnScreen[id].remove()
      // Also remove from markers cache to prevent unbounded growth
      delete markers[id]
      delete clusterImagesCache[Number(id)]
    }
  }

  markersOnScreen = newMarkers
}

const debouncedUpdateMarkers = debounce(updateMarkers, 50)

function onMoveEnd(): void {
  if (props.map.isSourceLoaded(props.sourceId)) {
    debouncedUpdateMarkers()
  }
}

function onSourceData(e: { sourceId?: string; isSourceLoaded?: boolean }): void {
  if (e.sourceId === props.sourceId && e.isSourceLoaded) {
    debouncedUpdateMarkers()
  }
}

watch(() => props.isVisible, () => updateMarkers())

onMounted(() => {
  props.map.on('moveend', onMoveEnd)
  props.map.on('sourcedata', onSourceData)
  if (props.map.isSourceLoaded(props.sourceId)) {
    updateMarkers()
  }
})

onUnmounted(() => {
  props.map.off('moveend', onMoveEnd)
  props.map.off('sourcedata', onSourceData)
  debouncedUpdateMarkers.cancel()

  // Clean up all markers
  Object.values(markers).forEach(marker => marker.remove())
  Object.keys(markers).forEach(key => delete markers[key])
  markersOnScreen = {}

  // Clear cache
  Object.keys(clusterImagesCache).forEach(key => delete clusterImagesCache[Number(key)])
})
</script>

<template>
  <!-- Markers are managed via DOM, no template needed -->
</template>

<style>
/* Image cluster marker */
.image-cluster-marker {
  cursor: pointer;
}

.cluster-image-container {
  position: relative;
  width: 76px;
  height: 76px;
  border-radius: 8px;
  overflow: hidden;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  border: 2px solid white;
}

/* Loading state - gray placeholder */
.cluster-image-container.loading {
  background: #e5e7eb;
}

/* Grid container */
.cluster-grid {
  width: 100%;
  height: 100%;
  display: grid;
  gap: 1px;
}

/* Grid layouts */
.grid-1x1 .cluster-grid {
  grid-template-columns: 1fr;
  grid-template-rows: 1fr;
}

.grid-2x1 .cluster-grid {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
}

.grid-2x2 .cluster-grid {
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
}

/* Grid cell (individual image) */
.cluster-grid-cell {
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-color: #e5e7eb;
}

/* Count in bottom-left corner - white text with shadow */
.cluster-count {
  position: absolute;
  bottom: 4px;
  left: 6px;
  color: white;
  font-size: 14px;
  font-weight: 700;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8), 0 0 8px rgba(0, 0, 0, 0.5);
}

/* Circle fallback for clusters without images */
.cluster-circle {
  width: 44px;
  height: 44px;
  background: #2259ff;
  border: 3px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
}

.cluster-circle-count {
  color: white;
  font-size: 14px;
  font-weight: 600;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Hover effects */
.image-cluster-marker:hover .cluster-image-container {
  transform: scale(1.05);
  transition: transform 0.15s ease;
}

.image-cluster-marker:hover .cluster-circle {
  transform: scale(1.1);
  transition: transform 0.15s ease;
}
</style>
